import { ModifierGroupRole, type ModifierGroupVariation } from '@sg/graphql-schema';

import {
  type Modification,
  type ModificationsMap,
  type ProductModification,
  type ProductModifierGroup,
  type ProductModifierGroups,
} from '../../types';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Generates a product modifications map from a list of product modifiers.
 */
export function generateModificationsMap(groups: ProductModifierGroups) {
  const extractedModifications = groups.flatMap(extendModifications);

  const allModificationsMap = extractedModifications.reduce(appendModification, new Map());
  const includedModificationsMap = extractedModifications.reduce(appendIncludedModification, new Map()); // prettier-ignore
  const extrasModificationsMap = extractedModifications.reduce(appendExtrasModification, new Map());

  return {
    allModificationsMap,
    includedModificationsMap,
    extrasModificationsMap,
  };
}

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Extends the modifications of the given modifier group by applying a factory function
 * to each modification within the group.
 */
function extendModifications(modifierGroup: ProductModifierGroup, index: number) {
  const { id, name, displayName, variation, modifications } = modifierGroup;

  const extendModification = extendModificationFactory({
    modificationGroupId: id,
    modificationGroupName: name,
    modificationGroupDisplayName: displayName,
    modificationGroupOrder: index,
    modificationGroupVariation: variation,
    modificationGroupRole: modifierGroup.groupRole,
    isExtra: modifierGroup.groupRole === ModifierGroupRole.Extras,
  });

  return modifications.map(extendModification);
}

/**
 * Appends a product modification to the given modifications map.
 */
function appendModification(modificationsMap: ModificationsMap, modification: Modification) {
  if (!modification.ingredient) {
    return modificationsMap;
  }

  const ingredientId = modification.ingredient.id;

  modificationsMap.set(ingredientId, modification);

  return modificationsMap;
}

/**
 * Appends a non-extra modification to the map, keyed by its base ingredient ID.
 */
function appendIncludedModification(
  modificationsMap: ModificationsMap,
  modification: Modification,
) {
  if (modification.isExtra || !modification.ingredientId) return modificationsMap;

  modificationsMap.set(modification.ingredientId, modification);

  return modificationsMap;
}

/**
 * Appends an extras modification to the map, keyed by its base ingredient ID.
 */
function appendExtrasModification(modificationsMap: ModificationsMap, modification: Modification) {
  if (!modification.isExtra || !modification.ingredientId) return modificationsMap;

  modificationsMap.set(modification.ingredientId, modification);

  return modificationsMap;
}

/**
 * Creates a function to extend a product modification with additional properties
 * including modification group ID and name, order, and a default quantity value.
 */
function extendModificationFactory(params: ExtendModificationFactoryParams) {
  const {
    modificationGroupId,
    modificationGroupName,
    modificationGroupDisplayName,
    modificationGroupOrder,
    modificationGroupVariation,
    modificationGroupRole,
    isExtra,
  } = params;

  return function (productModification: ProductModification, index: number): Modification {
    return {
      ...productModification,
      modificationGroupId,
      modificationGroupName,
      modificationGroupDisplayName,
      modificationGroupOrder,
      modificationGroupVariation,
      modificationGroupRole,
      order: index,
      quantity: 1,
      isExtra,
    };
  };
}

// ─── Types ───────────────────────────────────────────────────────────────────

type ExtendModificationFactoryParams = {
  modificationGroupId: string;
  modificationGroupName: string;
  modificationGroupDisplayName: string;
  modificationGroupOrder: number;
  modificationGroupVariation: ModifierGroupVariation;
  modificationGroupRole: ModifierGroupRole | null;
  isExtra: boolean;
};
